import { useCallback, useEffect, useRef, useState } from "react";

import { logger } from "@/infrastructure/lib/logger";
import { activeOrganizationHeader } from "@/shared/utils/fetchApi";
import { createAuthenticatedSSE, type SSESubscription } from "@/shared/utils/fetchSSE";

import { reconnectDelay } from "@/features/Jobs/utils/runs";
import { BackgroundTasksService } from "../services/backgroundTasksService";
import { BACKGROUND_TASKS_CHANGED_EVENT, isRunningTask, type BackgroundTask } from "../types/backgroundTasks";

/** Uma resposta que chegou numa conversa por conta de uma tarefa que terminou. */
export interface BackgroundReply {
  chatId: string;
  message: { id: string; content: string; metadata?: Record<string, unknown> | null };
}

/**
 * As tarefas em segundo plano do usuário no Playground (AB#86889, tasks 86915 e 87270).
 *
 * A sala SSE do usuário avisa quando uma tarefa muda e quando uma resposta chega numa conversa.
 * O aviso só diz "mudou": a lista é relida da api, que é a fonte. Assim o indicador da conversa
 * aberta e a marca na lista de conversas se atualizam sem recarregar a página. A conexão reabre
 * sozinha, com espera crescente, e na hora quando a aba volta a ficar visível — o que também
 * relê a conversa aberta, cobrindo o que mudou com a conexão caída.
 */
export function useBackgroundTasks(chatId: string | null | undefined, onReply?: (reply: BackgroundReply) => void) {
  const [tasks, setTasks] = useState<BackgroundTask[]>([]);
  /** Tarefas vivas por conversa, aprendidas pelos avisos; sem aviso, vale o que veio na lista. */
  const [counts, setCounts] = useState<Record<string, number>>({});
  /** Conversas com o agente respondendo agora, pelos avisos; sem aviso, vale o que veio na lista. */
  const [turns, setTurns] = useState<Record<string, boolean>>({});
  const replyHandler = useRef(onReply);
  replyHandler.current = onReply;
  const selected = useRef(chatId);
  selected.current = chatId;

  const refresh = useCallback(async (target: string) => {
    try {
      const list = await BackgroundTasksService.list(target);
      const running = list.filter(isRunningTask).length;
      setCounts((prev) => ({ ...prev, [target]: running }));
      if (target === selected.current) setTasks(list);
    } catch (error) {
      // O indicador fica com o último estado conhecido; o próximo aviso ou a volta à aba relê.
      logger.warn("[useBackgroundTasks] Could not read the background tasks", { chatId: target, error });
    }
  }, []);

  useEffect(() => {
    setTasks([]);
    if (chatId) void refresh(chatId);
  }, [chatId, refresh]);

  useEffect(() => {
    let subscription: SSESubscription | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      subscription?.close();
      subscription = createAuthenticatedSSE(BackgroundTasksService.streamUrl(), {
        headers: activeOrganizationHeader(),
        onOpen: () => {
          attempt = 0;
        },
        onMessage: (data, eventType) => {
          try {
            const payload = JSON.parse(data) as Record<string, unknown>;
            if (eventType === "background_task_changed") {
              const task = payload.task as BackgroundTask | undefined;
              if (task?.chat_id) void refresh(task.chat_id);
              window.dispatchEvent(new Event(BACKGROUND_TASKS_CHANGED_EVENT));
            } else if (eventType === "chat_activity_changed") {
              const target = payload.chat_id as string | undefined;
              if (target) setTurns((prev) => ({ ...prev, [target]: payload.turn_running === true }));
              window.dispatchEvent(new Event(BACKGROUND_TASKS_CHANGED_EVENT));
            } else if (eventType === "background_reply") {
              const target = payload.chat_id as string | undefined;
              const message = payload.message as BackgroundReply["message"] | undefined;
              if (target && message) replyHandler.current?.({ chatId: target, message });
            }
          } catch (error) {
            logger.warn("[useBackgroundTasks] Ignoring a malformed event", { eventType, error });
          }
        },
        onError: (error) => {
          logger.warn("[useBackgroundTasks] Background task stream dropped; reconnecting", { error: error.message });
          retry();
        },
        onClose: retry,
      });
    };

    function retry() {
      if (stopped) return;
      clearTimeout(timer);
      timer = setTimeout(connect, reconnectDelay(attempt++));
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      attempt = 0;
      clearTimeout(timer);
      connect();
      if (selected.current) void refresh(selected.current);
    };

    connect();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      subscription?.close();
    };
  }, [refresh]);

  const cancel = useCallback(
    async (taskId: string) => {
      if (!chatId) return;
      await BackgroundTasksService.cancel(chatId, taskId);
      await refresh(chatId);
      window.dispatchEvent(new Event(BACKGROUND_TASKS_CHANGED_EVENT));
    },
    [chatId, refresh]
  );

  /**
   * Quanto está acontecendo na conversa — tarefas vivas mais o turno do agente, se estiver
   * respondendo: a bolinha da lista. O aviso mais recente vale; sem ele, o que veio na lista.
   */
  const activityFor = useCallback(
    (chat: { id: string; background_tasks_count?: number; turn_running?: boolean }) =>
      (counts[chat.id] ?? chat.background_tasks_count ?? 0) + ((turns[chat.id] ?? chat.turn_running) ? 1 : 0),
    [counts, turns]
  );

  const running = tasks.filter(isRunningTask);
  return { tasks, running, runningCount: running.length, cancel, activityFor, refresh };
}
